import { MAX_N_HOTEL_IMAGES } from '@packages/media/mixins/hotel-images.mixin'

const isInImages = (images, image) => {
  if (!images) {
    return false
  }

  return images.some(i => i.image === image.image)
}

function processGiataData (giata, poProperty) {
  giata.description = giata.description_translationkey ? giata.description : giata.texts?.sections?.find(s => s?.type === 100)?.text
  giata.propertyName = giata.property.name
  giata.townName = giata.property.city
  giata.rating = +giata.property.rating
  giata.latitude = giata.property.geocodes?.latitude
  giata.longitude = giata.property.geocodes?.longitude
  giata.country = giata.property.country

  if (giata.highlighted_facilities) {
    giata.highlighted_facilities = typeof giata.highlighted_facilities === 'string'
      ? giata.highlighted_facilities.replace(/\s/g, '').split(',')
      : giata.highlighted_facilities
  }

  giata.name = giata.property.name
  giata.category = giata.property.category
  giata.lat = giata.property.geocodes[0]?.latitude
  giata.lng = giata.property.geocodes[0]?.longitude
  giata.images = giata.images
    .map((i) => {
      return {
        image: i.sizes,
        image_type: 'giata',
      }
    })
    .filter(i => !isInImages(poProperty?.images, i))

  return giata
}

function processExpediaData (expedia) {
  expedia.description = expedia.descriptions?.location
  expedia.propertyName = expedia.name
  expedia.townName = expedia.address?.city
  expedia.latitude = expedia.location?.latitude
  expedia.longitude = expedia.location?.longitude
  expedia.country = expedia.address?.country_code
  expedia.images = []

  return expedia
}

function processHotelbedsData (hotelbeds, poProperty) {
  hotelbeds.description = hotelbeds.meta?.Description
  hotelbeds.propertyName = hotelbeds.meta?.PropertyName
  hotelbeds.townName = hotelbeds.meta?.TownCity
  hotelbeds.rating = hotelbeds.meta?.Rating
  hotelbeds.latitude = hotelbeds.meta?.Latitude
  hotelbeds.longitude = hotelbeds.meta?.Longitude
  hotelbeds.country = hotelbeds.meta?.Country
  hotelbeds.highlighted_facilities = hotelbeds.meta?.highlighted_facilities
  hotelbeds.rating = +hotelbeds.meta?.Rating
  hotelbeds.images = hotelbeds.meta.images
    .map((image) => {
      return {
        image,
        image_type: hotelbeds.meta.images_type,
      }
    })
    .filter(i => !isInImages(poProperty?.images, i))

  return hotelbeds
}

function processPoPropertyData (poProperty, giata) {
  poProperty.propertyName = poProperty.name
  poProperty.townName = poProperty.city

  const removeImages = poProperty?.images?.map(i => i.image)
  poProperty.images = poProperty?.images?.map((i) => {
    if (i.image_type === 'giata') {
      return giata?.images.find((g) => {
        return g.image.some(giataImage => giataImage.href.match(i.image) !== null)
      })
    }
    return i
  })
  if (poProperty.images?.length > 0 && giata?.images) {
    giata.images = giata.images.filter((i) => {
      return !i.image
        .some((giataImage) => {
          return removeImages.some(ri => giataImage.href.match(ri))
        })
    })
  }

  poProperty.lat = poProperty.latitude
    ? parseFloat(poProperty.latitude)
    : undefined
  poProperty.lng = poProperty.longitude
    ? parseFloat(poProperty.longitude)
    : undefined

  return poProperty
}

const definedProps = (obj) => {
  if (!obj) {
    return
  }
  return Object.fromEntries(
    Object.entries(obj).filter(([,v]) => v !== undefined && !!v)
  )
}

export const getHotelGiataId = (hotel) => {
  return hotel.hotelbeds?.giataId ?? hotel.poProperty?.giata_id ?? hotel.giata?.property?.giata_id ?? hotel.giata?.property?.giataId
}

export function processHotelInfo (hotel, { hotelDescriptionAI } = {}) {
  // eslint-disable-next-line camelcase, prefer-const
  const { info, giata, expedia, hotelbeds, poProperty, tripadvisor_data, stopOvers, charter_tags: charterTags, destinations, dates } = deepClone(hotel)
  const [destination] = destinations || [null]

  if (giata) {
    processGiataData(giata, poProperty)
  }
  if (expedia) {
    processExpediaData(expedia)
  }
  if (hotelbeds) {
    processHotelbedsData(hotelbeds, poProperty)
  }
  if (poProperty) {
    processPoPropertyData(poProperty, giata)
  }

  const images = [
    ...(poProperty?.images || []),
    ...(hotelbeds?.images || giata?.images || expedia?.images || []),
  ]
    .slice(0, MAX_N_HOTEL_IMAGES)
    .filter(Boolean)

  let hotelImage = {}
  if (images && images.length) {
    const image = Array.isArray(images[0]?.image) ? images[0].image[images[0].image.length - 1] : images[0]

    hotelImage = {
      url: typeof image !== 'string'
        ? (image?.href || image?.image || image?.url)
        : image,
      type: image?.href
        ? 'unknown'
        : image?.image_type,
    }
  } else if (hotel.main_image || hotel.main_picture_cloudinary) {
    hotelImage = {
      url: hotel.main_image || hotel.main_picture_cloudinary,
      type: hotel.main_picture_cloudinary ? 'cloudinary' : hotel.image_type,
    }
  }

  let formattedHotelDescriptionAI
  if (hotelDescriptionAI?.length) {
    formattedHotelDescriptionAI = hotelDescriptionAI
      .reduce((html, descriptionSection) =>
        html + `
          <h4 class="text-lg -mb-4 -mt-2">${descriptionSection.title}</h4>
          <p>${descriptionSection.description}</p>`,
      ''
      )
  }

  const description = (
    formattedHotelDescriptionAI ||
    info ||
    poProperty?.description ||
    (giata?.description_translationkey ? giata?.description : null) ||
    (`${hotelbeds?.meta?.Description}`.length > `${giata?.description}`.length ? hotelbeds?.meta?.Description : giata?.description) ||
    hotelbeds?.meta?.shortDescription ||
    hotelbeds?.meta?.longDescription ||
    expedia?.description
  )

  let allowHtmlDescription = false
  if (formattedHotelDescriptionAI || description === poProperty?.description) {
    allowHtmlDescription = true
  }

  return {
    stopOvers,
    hotelImage,
    tripadvisor_data,
    destination,
    dates,
    info: hotel.info,
    charterTags: charterTags || [],
    giataId: getHotelGiataId(hotel),
    ...definedProps(expedia),
    ...definedProps(hotelbeds),
    ...definedProps(giata),
    ...definedProps(poProperty),
    allowHtmlDescription,
    description,
    description_translationkey: poProperty?.description_translationkey,
    supplier: 'giata',
    images,
    propertyName: giata?.propertyName || poProperty?.propertyName || hotelbeds?.propertyName || expedia?.propertyName,
    property_id: poProperty?.giata_id || giata?.property?.giataId,
    displayDestination: {
      destination: destination?.destination_name,
      zone: poProperty?.zone_name || giata?.property?.city,
      country: destination?.country_name,
    },
    link: (poProperty?.homepage_url || giata?.property.urls[0])?.replace('http:', 'https:'),
    trips: hotel.trips,
  }
}
